import React from 'react'
import { FormItemProps, FormItem } from './FormItem'
import { Checkbox } from './Checkbox'
import { TextField } from './TextField'
import { ShowIf } from './ShowIf'
import { IField, validateFieldsEx } from './FieldValidation'
import { AddressPicker, AddressOption } from './AddressPicker'
import { Option } from '../../models/Option'
import { TextSelectField } from './SelectField'

export interface Address {
    value: string;
    label: string;
    custom: boolean;
    line1: string;
    line2: string;
    postalCode: string;
    suburb: string;
    state: string;
    country: string;
}

export const emptyAddress: Readonly<Address> = {
  value: '',
  label: '',
  custom: false,
  line1: '',
  line2: '',
  postalCode: '',
  suburb: '',
  state: '',
  country: '',
}

export interface AddressFieldProps extends FormItemProps {
    id: string
    value: Address
    onChange: { (v: Address): void }
    required?: boolean
    requiredMessage?: string
    disabled?: boolean
    australianAddressesOnly?: boolean
    countries: Option<string>[]
}

export interface AddressFieldState {
    selectedAddress?: AddressOption
}

const AUSTRALIA = 'Australia'

declare let google: any

export class AddressField extends React.Component<AddressFieldProps, AddressFieldState> implements IField {
  service: any
  pickerField: AddressPicker
  line1Field: TextField
  line2Field: TextField
  suburbField: TextField
  stateFieldText: TextField
  stateFieldPick: TextSelectField
  postalCodeField: TextField
  countryField: TextSelectField

  constructor(props: AddressFieldProps, context?: any) {
    super(props, context)

    if (props.value) {
      this.state = {
        selectedAddress: {
          value: props.value.value || '', // Handle null value
          label: props.value.label,
        },
      }
    } else this.state = {}
  }

  componentDidMount() {
    this.service = new google.maps.places.PlacesService(document.createElement('span'))
  }

  componentDidUpdate(prevProps: AddressFieldProps, prevState: AddressFieldState) {
    const { value } = this.props
    if (value !== prevProps.value) {
      // bug fix: .value can be null, yet label can have a valid address to be displayed.
      this.setState({
        selectedAddress: value && value.value ? { value: value.value, label: value.label } : { value: '', label: value.label },
      });
    }
  }

  // expose selection externally, for use by RelatedCompanyDetails.tsx
  getSelectedCountry() {
    const { value } = this.props
    return value ? value.country : null
  }


  onChange(v: Partial<Address>) {
    const { value, australianAddressesOnly, onChange } = this.props
    const temp = { ...value, ...v }
    if (temp.custom) {
      let label = ''
      const parts = [temp.line1, temp.line2, temp.suburb, temp.state, temp.postalCode, temp.country]
      for (const p of parts) {
        if (!p || p.length == 0) {
          continue
        }
        if (label.length > 0) {
          label += ', '
        }
        label += p
      }

      temp.label = label

      if (australianAddressesOnly) {
        temp.country = AUSTRALIA
      }
    }
    onChange(temp)
  }

  onPick(v: AddressOption) {
    const addressParts: any = {
      street_number: { type: 'short_name', field: null },
      route: { type: 'long_name', field: 'line1' },
      neighborhood: { type: 'long_name', field: 'line2' },
      sublocality_level_3: { type: 'long_name', field: 'line2' },
      sublocality_level_2: { type: 'long_name', field: 'line2' },
      sublocality_level_1: { type: 'long_name', field: 'line2' },
      locality: { type: 'long_name', field: 'suburb' },
      administrative_area_level_1: { type: 'short_name', field: 'state' },
      country: { type: 'long_name', field: 'country' },
      postal_code: { type: 'short_name', field: 'postalCode' },
    }

    this.setState({ selectedAddress: v }, () => {
      if (v == null) {
        const res: any = {
          value: null,
          label: null,
        }
        for (const part in addressParts) {
          if (addressParts[part].field != null) {
            res[addressParts[part].field] = null
          }
        }

        this.onChange(res)
      } else {
        const res: any = {
          value: v.value,
          label: v.label,
        }

        this.service.getDetails({ placeId: v.value }, (place: any, status: any) => {
          let temp: string = null

          if (place != null) {
            for (let i = 0; i < place.address_components.length; i++) {
              const part = place.address_components[i].types[0]
              if (part in addressParts) {
                const val = place.address_components[i][addressParts[part].type]
                if (addressParts[part].field != null) {
                  if (res[addressParts[part].field] == null) res[addressParts[part].field] = val
                  else res[addressParts[part].field] += `, ${val}`
                } else {
                  temp = val
                }
              }
            }
          } else {
            for (const part in addressParts) {
              if (addressParts[part].field != null) {
                res[addressParts[part].field] = null
              }
            }
          }

          if (temp != null) {
            res.line1 = `${temp}`
                            + `${isNaN(temp as any) ? ', ' : ' '}`
                            + `${res.line1}`
          }

          this.onChange(res)
        })
      }
    })
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    validateFieldsEx(
      focus,
      callback,
      this.pickerField,
      this.line1Field,
      this.line2Field,
      this.suburbField,
      this.stateFieldText,
      this.stateFieldPick,
      this.postalCodeField,
      this.countryField,
    )
  }

  render() {
    const states = [
      { value: 'ACT', label: 'ACT' },
      { value: 'NSW', label: 'NSW' },
      { value: 'NT', label: 'NT' },
      { value: 'QLD', label: 'QLD' },
      { value: 'SA', label: 'SA' },
      { value: 'TAS', label: 'TAS' },
      { value: 'VIC', label: 'VIC' },
      { value: 'WA', label: 'WA' },
    ]
    const { selectedAddress } = this.state

    const {
      value, preview, australianAddressesOnly, required, visible, id, disabled, requiredMessage, srOnlyLabel, label,
    } = this.props
    let { countries } = this.props

    const isCountryAustralia = value && value.country === AUSTRALIA

    let temp: Address
    if (value) {
      temp = { ...emptyAddress, ...value }
    } else {
      temp = { ...emptyAddress }
    }

    if (preview) {
      return (
        <FormItem {...this.props}>
          <div className="form-value">{temp.label ? temp.label : '--'}</div>
        </FormItem>
      )
    }

    if (australianAddressesOnly) {
      countries = countries.filter((c) => c.label === AUSTRALIA)
    } else if (temp.country && countries.filter((r) => r.value === temp.country).length === 0) {
      countries = countries.slice()
      countries.push({ value: temp.country, label: temp.country })
    }

    const pickerRequired = (required && !temp.custom)
    const fieldRequired = (required && temp.custom)
    return (
      <div style={{ display: visible === false ? 'none' : '' }}>
        <AddressPicker
          id={`${id}_picker`}
          ref={(r) => { this.pickerField = r }}
          disabled={disabled}
          value={selectedAddress}
          onChange={(v) => this.onPick(v)}
          label={label}
          srOnlyLabel={srOnlyLabel}
          required={pickerRequired}
          requiredMessage={requiredMessage}
          australianAddressesOnly={australianAddressesOnly}
        />
        <div className="form-item">
          <Checkbox
            id={`${id}_custom`}
            disabled={disabled}
            label="I cannot find the exact address (enter manually)"
            value={temp.custom}
            onChange={(v) => this.onChange({ custom: v })}
          />
        </div>
        <ShowIf condition={temp.custom} className="form-section">
          <TextField
            id={`${id}_line1`}
            ref={(r) => { this.line1Field = r }}
            disabled={disabled}
            label="Street address line 1"
            value={temp.line1}
            onChange={(v) => this.onChange({ line1: v })}
            required={fieldRequired}
            maxLength={50}
            className="mt-0"
          />
          <TextField
            id={`${id}_line2`}
            ref={(r) => { this.line2Field = r }}
            disabled={disabled}
            label="Street address line 2"
            value={temp.line2}
            onChange={(v) => this.onChange({ line2: v })}
            maxLength={50}
          />
          <TextField
            id={`${id}_suburb`}
            ref={(r) => { this.suburbField = r }}
            disabled={disabled}
            label="Suburb"
            value={temp.suburb}
            onChange={(v) => this.onChange({ suburb: v })}
            maxLength={80}
            required={fieldRequired}
          />
          <div className="clearfix">
            {isCountryAustralia
              ? (
                <div className="float-left mr-3" style={{ minWidth: '150px' }}>
                  <TextSelectField
                      id={`${id}_state`}
                      ref={(r) => { this.stateFieldPick = r }}
                      disabled={disabled}
                      label="State"
                      value={temp.state}
                      onChange={(v) => this.onChange({ state: v })}
                      required={fieldRequired && isCountryAustralia}
                      dataSource={states}
                      block
                      position="relative"
                  />
                </div>
              )
              : (
                <TextField
                    className="float-left mr-3"
                    id={`${id}_state`}
                    ref={(r) => { this.stateFieldText = r }}
                    disabled={disabled}
                    label="State"
                    value={temp.state}
                    onChange={(v) => this.onChange({ state: v })}
                    required={fieldRequired && isCountryAustralia}
                    maxLength={isCountryAustralia ? 4 : 50}
                    block={false}
                />
              )}
            <TextField
                className="float-left mr-3"
                id={`${id}_postalCode`}
                ref={(r) => { this.postalCodeField = r }}
                disabled={disabled}
                label="Postcode"
                value={temp.postalCode}
                onChange={(v) => this.onChange({ postalCode: v })}
                required={fieldRequired && isCountryAustralia}
                maxLength={isCountryAustralia ? 4 : 50}
                block={false}
            />
            <div className="float-left" style={{ minWidth: '300px' }}>
              <TextSelectField
                  id={`${id}_country`}
                  ref={(r) => { this.countryField = r }}
                  disabled={disabled}
                  label="Country"
                  value={temp.country}
                  onChange={(v) => this.onChange({ country: v })}
                  required={fieldRequired}
                  dataSource={countries}
                  block={false}
              />
            </div>
          </div>
        </ShowIf>
      </div>
    )
  }
}
